import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY_FORM = { prospect_first_name: "", prospect_last_name: "", prospect_phone: "", prospect_city: "", remarks: "" };

// The member capture path refuses a self referral and a prospect who is already
// inside another member's 90-day window; both arrive as bare detail codes.
const CAPTURE_ERROR_COPY = {
  self_referral: "You cannot refer your own phone number.",
  already_referred: "This prospect has already been referred.",
};

export const AddReferralDrawer = ({ open, setOpen, memberId, onCreated }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/referrals", { referrer_member_id: memberId, ...form });
      // A prospect already inside another member's 90-day window comes back as
      // a 200 carrying a status, not an error, so keep the form open and filled.
      if (data?.status === "already_referred") {
        toast.error(CAPTURE_ERROR_COPY.already_referred);
        return;
      }
      if (data.fraud_flag) toast.warning("Referral submitted for review");
      else toast.success("Referral added successfully");
      setForm(EMPTY_FORM);
      setOpen(false);
      onCreated();
    } catch (error) {
      const copy = CAPTURE_ERROR_COPY[error?.response?.data?.detail];
      toast.error(copy || formatApiError(error, "Could not add referral"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent className="mx-auto max-h-[92vh] max-w-xl overflow-y-auto rounded-t-[26px] bg-brand-canvas">
        <form data-testid="add-referral-form" onSubmit={submit}>
          <DrawerHeader className="px-6 pb-2 text-left">
            <DrawerTitle className="brand-title text-2xl">Refer a friend</DrawerTitle>
            <DrawerDescription>We’ll track commercial progress from Greentek ERP. First valid attribution wins.</DrawerDescription>
          </DrawerHeader>
          <div className="space-y-4 px-6 py-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2"><div><Label htmlFor="friend-first-name">First name</Label><Input data-testid="referral-first-name-input" id="friend-first-name" required minLength={1} value={form.prospect_first_name} onChange={(event) => setForm((current) => ({ ...current, prospect_first_name: event.target.value }))} placeholder="e.g. Ananya" /></div><div><Label htmlFor="friend-last-name">Last name</Label><Input data-testid="referral-last-name-input" id="friend-last-name" value={form.prospect_last_name} onChange={(event) => setForm((current) => ({ ...current, prospect_last_name: event.target.value }))} placeholder="e.g. Rao" /></div></div>
            <div><Label htmlFor="friend-mobile">Mobile number</Label><Input data-testid="referral-phone-input" id="friend-mobile" inputMode="numeric" required pattern="[0-9]{10}" maxLength={10} value={form.prospect_phone} onChange={(event) => setForm((current) => ({ ...current, prospect_phone: event.target.value.replace(/\D/g, "") }))} placeholder="10-digit mobile" /></div>
            <div><Label htmlFor="friend-city">City</Label><Input data-testid="referral-city-input" id="friend-city" value={form.prospect_city} onChange={(event) => setForm((current) => ({ ...current, prospect_city: event.target.value }))} placeholder="Hyderabad" /></div>
            <div><div className="flex items-center justify-between"><Label htmlFor="referral-remarks">Description / Remarks</Label><span className="text-[11px] text-[#71807d]">Optional · {form.remarks.length}/500</span></div><Textarea data-testid="referral-remarks-input" id="referral-remarks" maxLength={500} rows={4} value={form.remarks} onChange={(event) => setForm((current) => ({ ...current, remarks: event.target.value }))} placeholder="Add the prospect’s requirement, preferred system size, roof details, or any useful context" /></div>
            <div className="privacy-note">By submitting, you confirm your friend has agreed to be contacted by Greentek.</div>
          </div>
          <DrawerFooter className="px-6 pb-6"><Button data-testid="add-referral-submit-button" disabled={saving} type="submit" className="h-12">{saving ? "Submitting…" : "Submit referral"}</Button><DrawerClose asChild><Button type="button" variant="ghost">Cancel</Button></DrawerClose></DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
